import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
    Search, Loader2, Store, Star, Send, ShieldCheck, MapPin, Briefcase,
    Clock, DollarSign, Filter, ArrowRight, Sparkles, Zap, MessageCircle, Lock, Unlock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { toast } from "@/components/ui/sonner";
import { TrustBreakdownCard } from "@/components/TrustBreakdownCard";
import MessageComposeDialog from "@/components/messaging/MessageComposeDialog";

const PAGE_SIZE = 24;
const MIN_EXP_OPTIONS = [
    { v: "0", label: "Any experience" },
    { v: "1", label: "1+ years" },
    { v: "3", label: "3+ years" },
    { v: "5", label: "5+ years" },
    { v: "8", label: "8+ years" },
    { v: "10", label: "10+ years" },
];
const MIN_TRUST_OPTIONS = [
    { v: "0", label: "Any verification level" },
    { v: "60", label: "Some verifications" },
    { v: "75", label: "Verified passport" },
    { v: "90", label: "Fully verified" },
];

const trustTone = (t) => {
    if (t >= 85) return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30";
    if (t >= 70) return "bg-sky-500/15 text-sky-700 dark:text-sky-400 border-sky-500/30";
    return "bg-slate-500/15 text-slate-600 dark:text-slate-400 border-slate-500/30";
};

const CandidateCard = ({ c, isFavorite, request, onFavorite, onReachOut, onViewPassport, onMessage, onUnlockContact, unlocked, quotaRemaining }) => {
    const consent = request?.consent_status || null;
    const canMessage = Boolean(c.talent_user_id);
    return (
    <motion.div
        initial={{ opacity: 0, y: 8 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
        data-testid={`marketplace-card-${c.id}`}
        className={`card-soft p-5 flex flex-col group transition-colors ${c.boosted ? "border-amber-500/50 ring-1 ring-amber-500/30 hover:border-amber-500/70" : "hover:border-emerald-500/40"}`}
    >
        {c.boosted && (
            <div data-testid={`marketplace-boosted-${c.id}`} className="mb-3 inline-flex self-start items-center gap-1 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">
                <Zap size={10} /> Boosted · Top pick
            </div>
        )}
        <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
                {/* Avatar rollout iter 93 — anonymized marketplace card.
                    ``url`` intentionally forced null since the marketplace
                    hides candidate identity behind initials; the Avatar
                    still gives us a deterministic per-candidate COLOR
                    (varies by ``userId``) so cards look distinct instead
                    of every card sharing the same emerald-sky gradient. */}
                <Avatar
                    url={null}
                    initials={c.initials}
                    userId={c.id}
                    size={44}
                    ring={false}
                    testId={`marketplace-avatar-${c.id}`}
                />
                <div className="min-w-0">
                    <div className="font-heading font-semibold text-foreground leading-snug truncate">
                        {c.desired_position || "Open to opportunities"}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                        {c.city && (
                            <span className="inline-flex items-center gap-1"><MapPin size={11} />{c.city}</span>
                        )}
                        {c.years_experience && (
                            <span className="inline-flex items-center gap-1"><Briefcase size={11} />{c.years_experience} yrs</span>
                        )}
                    </div>
                </div>
            </div>
            <button
                onClick={() => onFavorite(c)}
                data-testid={`marketplace-fav-${c.id}`}
                aria-label={isFavorite ? "Remove favorite" : "Add favorite"}
                className={`shrink-0 rounded-full p-1.5 border transition-colors ${isFavorite ? "border-amber-400 bg-amber-50 dark:bg-amber-500/10 text-amber-500" : "border-border text-muted-foreground hover:text-amber-500 hover:border-amber-400"}`}
            >
                <Star size={14} fill={isFavorite ? "currentColor" : "none"} />
            </button>
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
            {/* iter-168 — Trust Score + opaque breakdown removed from browse per
                EMPLOYER_BROWSE_MATCH_v1 spec §5. Verification is now a plain
                badge; the full factor breakdown lives on /employer/jobs/:id/browse. */}
            <Badge className="text-[10px] bg-sky-500/15 text-sky-700 dark:text-sky-400 border border-sky-500/30 inline-flex items-center gap-1">
                <ShieldCheck size={10} /> Verified passport
            </Badge>
            {c.availability && (
                <Badge variant="outline" className="text-[10px] border-border">
                    <Clock size={10} className="mr-1" />{c.availability}
                </Badge>
            )}
            {c.desired_salary && (
                <Badge variant="outline" className="text-[10px] border-border">
                    <DollarSign size={10} className="mr-1" />{c.desired_salary}
                </Badge>
            )}
        </div>

        {c.languages_spoken && (
            <div className="mt-3 text-[11px] text-muted-foreground line-clamp-1">
                <span className="font-semibold text-foreground">Languages:</span> {c.languages_spoken}
            </div>
        )}
        {c.work_authorization && (
            <div className="mt-1 text-[11px] text-muted-foreground line-clamp-1">
                <span className="font-semibold text-foreground">Work auth:</span> {c.work_authorization}
            </div>
        )}
        {c.skills?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
                {c.skills.slice(0, 6).map((s, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border">
                        {s}
                    </span>
                ))}
            </div>
        )}

        <div className="mt-auto pt-4 border-t border-border flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                {consent === "consented" ? "Passport unlocked" :
                    consent === "declined" ? "Candidate declined" :
                        consent === "pending" ? "Awaiting consent" : "Anonymized profile"}
            </span>
            <div className="inline-flex items-center gap-1.5 flex-wrap">
                {canMessage && (
                    <button
                        onClick={() => onMessage(c)}
                        data-testid={`marketplace-message-${c.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/15"
                        title="Send a direct message"
                    >
                        <MessageCircle size={10} /> Message
                    </button>
                )}
                {canMessage && !unlocked && (
                    <button
                        onClick={() => onUnlockContact(c)}
                        data-testid={`marketplace-unlock-${c.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-1 text-[11px] font-semibold text-foreground hover:bg-muted/50"
                        title={typeof quotaRemaining === "number" ? `${quotaRemaining} unlocks left today` : "Unlock contact info"}
                    >
                        <Lock size={10} /> Unlock contact info
                    </button>
                )}
                {canMessage && unlocked && (
                    <span
                        data-testid={`marketplace-unlocked-${c.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-400"
                    >
                        <Unlock size={10} /> Contact unlocked
                    </span>
                )}
                {consent === "consented" ? (
                    <button
                        onClick={() => onViewPassport(request)}
                        data-testid={`marketplace-view-${c.id}`}
                        className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1"
                    >
                        View Passport <ArrowRight size={11} className="ml-0.5" />
                    </button>
                ) : (
                    <button
                        onClick={() => onReachOut(c)}
                        disabled={!!consent}
                        data-testid={`marketplace-reach-${c.id}`}
                        className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                    >
                        {consent === "pending" ? "Awaiting" : consent === "declined" ? "Declined" : "Reach out"}
                        <ArrowRight size={11} className="ml-0.5" />
                    </button>
                )}
            </div>
        </div>
    </motion.div>
    );
};

const TalentMarketplace = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ results: [], total: 0, favorite_count: 0 });
    const [facets, setFacets] = useState({ availability: [], work_authorization: [] });
    const [requests, setRequests] = useState([]);

    const [q, setQ] = useState("");
    const [availability, setAvailability] = useState("any");
    const [workAuth, setWorkAuth] = useState("any");
    const [minExp, setMinExp] = useState("0");
    const [minTrust, setMinTrust] = useState("0");
    const [hasResume, setHasResume] = useState(false);
    const [onlyFav, setOnlyFav] = useState(false);
    const [verifiedOnly, setVerifiedOnly] = useState(false);
    const [page, setPage] = useState(1);

    const [reachTarget, setReachTarget] = useState(null);
    const [reachRole, setReachRole] = useState("");
    const [reachNote, setReachNote] = useState("");
    const [reachSubmitting, setReachSubmitting] = useState(false);

    const [passportRequest, setPassportRequest] = useState(null);
    const [passportData, setPassportData] = useState(null);
    const [passportLoading, setPassportLoading] = useState(false);
    // iter 149 — Stripe Identity request state for the passport dialog.
    const [idvStatus, setIdvStatus] = useState(null);
    const [idvSubmitting, setIdvSubmitting] = useState(false);

    // iter-165 — Messaging + Passport contact-unlock state. ``messageTarget``
    // controls the compose dialog; ``unlockState`` caches the employer's
    // daily quota + which talent_user_ids are already revealed today.
    const [messageTarget, setMessageTarget] = useState(null);
    const [unlockState, setUnlockState] = useState({
        unlocked_talent_ids: [], remaining: null, cap_per_day: null, unlimited: false,
    });
    const refreshUnlockQuota = useCallback(async () => {
        try {
            const { data } = await api.get("/employer/passport-unlocks/quota");
            setUnlockState({
                unlocked_talent_ids: data.unlocked_talent_ids || [],
                remaining: data.remaining,
                cap_per_day: data.cap_per_day,
                unlimited: !!data.unlimited,
            });
        } catch { /* non-fatal — buttons will still try the unlock endpoint */ }
    }, []);
    useEffect(() => { refreshUnlockQuota(); }, [refreshUnlockQuota]);

    const openMessage = (c) => setMessageTarget(c);
    const handleUnlock = async (c) => {
        if (!c.talent_user_id) {
            toast.error("This profile isn't fully linked yet — try Reach out instead.");
            return;
        }
        try {
            const { data } = await api.post("/employer/passport-unlocks/unlock", { talent_user_id: c.talent_user_id });
            if (data.already_unlocked) {
                toast("Contact info is already unlocked for today.");
            } else {
                toast.success("Contact info unlocked for the rest of the day.");
            }
            refreshUnlockQuota();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                q, min_experience: minExp, min_trust: minTrust,
                has_resume: hasResume, only_favorites: onlyFav,
                verified_only: verifiedOnly,
                page, page_size: PAGE_SIZE,
            };
            if (availability !== "any") params.availability = availability;
            if (workAuth !== "any") params.work_auth = workAuth;
            const [res, reqs] = await Promise.all([
                api.get("/employer/marketplace", { params }),
                api.get("/employer/marketplace/requests"),
            ]);
            setData(res.data);
            setRequests(reqs.data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [q, availability, workAuth, minExp, minTrust, hasResume, onlyFav, verifiedOnly, page]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        api.get("/employer/marketplace/facets")
            .then((r) => setFacets(r.data))
            .catch(() => { });
    }, []);

    // Reset page when filters change
    useEffect(() => { setPage(1); }, [q, availability, workAuth, minExp, minTrust, hasResume, onlyFav, verifiedOnly]);

    const requestByCandidate = useMemo(() => {
        const m = new Map();
        for (const r of requests) m.set(r.candidate_id, r);
        return m;
    }, [requests]);

    const toggleFavorite = async (c) => {
        try {
            const next = !c.favorite;
            await api.post("/employer/marketplace/favorites", { candidate_id: c.id, favorite: next });
            // Optimistic update
            setData((d) => ({
                ...d,
                favorite_count: d.favorite_count + (next ? 1 : -1),
                results: d.results.map((x) => x.id === c.id ? { ...x, favorite: next } : x),
            }));
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openReach = (c) => {
        setReachTarget(c);
        setReachRole(c.desired_position || "");
        setReachNote("");
    };

    const openPassport = async (req) => {
        setPassportRequest(req);
        setPassportData(null);
        setIdvStatus(null);
        setPassportLoading(true);
        try {
            const { data } = await api.get(`/employer/passport-requests/${req.id}/passport`);
            setPassportData(data);
            // iter 149 — fetch current identity-verification state for this candidate.
            const candidateId = data?.candidate?.id || req.candidate_id;
            if (candidateId) {
                try {
                    const { data: idv } = await api.get(`/employer/candidates/${candidateId}/id-verification`);
                    setIdvStatus(idv);
                } catch { /* non-fatal — dialog still renders */ }
            }
        } catch (err) {
            toast.error(formatApiError(err));
            setPassportRequest(null);
        } finally {
            setPassportLoading(false);
        }
    };

    const requestIdVerification = async () => {
        const candidateId = passportData?.candidate?.id || passportRequest?.candidate_id;
        if (!candidateId) return;
        setIdvSubmitting(true);
        try {
            const { data } = await api.post(`/employer/candidates/${candidateId}/id-verification/request`);
            if (data.created) {
                toast.success("Identity verification requested. The candidate will get an in-app and email ping.");
            } else {
                toast("A verification request is already open for this candidate.");
            }
            // Refresh state.
            const { data: idv } = await api.get(`/employer/candidates/${candidateId}/id-verification`);
            setIdvStatus(idv);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setIdvSubmitting(false);
        }
    };

    const submitReach = async () => {
        if (!reachTarget) return;
        try {
            setReachSubmitting(true);
            await api.post("/employer/marketplace/reach-out", {
                candidate_id: reachTarget.id,
                role_context: reachRole,
                note: reachNote,
            });
            toast.success("Reach-out sent. Our team will follow up.");
            setReachTarget(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setReachSubmitting(false);
        }
    };

    const totalPages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));

    return (
        <div data-testid="talent-marketplace-page">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
                <div>
                    <p className="overline">Self-serve sourcing</p>
                    <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                        Talent Marketplace<sup className="text-xs align-super">™</sup>
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Anonymized Talent Passports, filtered on your terms. Star candidates, then reach out — our team brokers the intro.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
                        <Store size={11} className="mr-1" /> {data.total} candidates
                    </Badge>
                    <Badge className="bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
                        <Star size={11} className="mr-1" /> {data.favorite_count} favorites
                    </Badge>
                    <Badge
                        data-testid="marketplace-unlock-quota"
                        className="bg-sky-500/15 text-sky-700 dark:text-sky-400 border border-sky-500/30"
                        title="Daily contact-info unlocks for your plan"
                    >
                        <Unlock size={11} className="mr-1" />
                        {unlockState.unlimited
                            ? "Unlimited unlocks"
                            : (unlockState.remaining == null
                                ? "Unlocks loading"
                                : `${unlockState.remaining} unlocks left`)}
                    </Badge>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
                {/* Filter sidebar */}
                <aside className="card-soft p-5 h-fit lg:sticky lg:top-24" data-testid="marketplace-filters">
                    <div className="flex items-center gap-2 mb-4">
                        <Filter size={14} className="text-emerald-500" />
                        <span className="font-heading font-semibold text-sm text-foreground">Filters</span>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Search</label>
                            <div className="relative mt-1">
                                <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Role, city, skill…" data-testid="marketplace-q" className="pl-8 h-9 text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Verification level</label>
                            <Select value={minTrust} onValueChange={setMinTrust}>
                                <SelectTrigger data-testid="marketplace-min-trust" className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {MIN_TRUST_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Experience</label>
                            <Select value={minExp} onValueChange={setMinExp}>
                                <SelectTrigger data-testid="marketplace-min-exp" className="h-9 mt-1 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {MIN_EXP_OPTIONS.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Availability</label>
                            <Select value={availability} onValueChange={setAvailability}>
                                <SelectTrigger data-testid="marketplace-availability" className="h-9 mt-1 text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="any">Any availability</SelectItem>
                                    {facets.availability.map((f) => (
                                        <SelectItem key={f.value} value={f.value}>{f.value} ({f.count})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Work Auth</label>
                            <Select value={workAuth} onValueChange={setWorkAuth}>
                                <SelectTrigger data-testid="marketplace-work-auth" className="h-9 mt-1 text-sm"><SelectValue placeholder="Any" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="any">Any authorization</SelectItem>
                                    {facets.work_authorization.map((f) => (
                                        <SelectItem key={f.value} value={f.value}>{f.value} ({f.count})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer" data-testid="marketplace-has-resume">
                            <input
                                type="checkbox"
                                checked={hasResume}
                                onChange={(e) => setHasResume(e.target.checked)}
                                className="rounded border-border accent-emerald-500"
                            />
                            Has resume uploaded
                        </label>
                        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-2 py-1.5" data-testid="marketplace-verified-only">
                            <input
                                type="checkbox"
                                checked={verifiedOnly}
                                onChange={(e) => setVerifiedOnly(e.target.checked)}
                                className="rounded border-border accent-emerald-500"
                            />
                            <ShieldCheck size={13} className="text-emerald-600" />
                            <span>Verified only <span className="text-[10px] text-muted-foreground">(2+ checks)</span></span>
                        </label>
                        <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer" data-testid="marketplace-only-fav">
                            <input
                                type="checkbox"
                                checked={onlyFav}
                                onChange={(e) => setOnlyFav(e.target.checked)}
                                className="rounded border-border accent-emerald-500"
                            />
                            Show only my favorites
                        </label>
                    </div>
                </aside>

                {/* Results */}
                <div>
                    {loading ? (
                        <div className="py-16 flex justify-center">
                            <Loader2 size={18} className="animate-spin text-emerald-500" />
                        </div>
                    ) : data.results.length === 0 ? (
                        <div className="card-soft p-12 text-center" data-testid="marketplace-empty">
                            <Store size={22} className="mx-auto text-emerald-500" />
                            <h3 className="mt-3 font-heading font-semibold text-xl text-foreground">No candidates match those filters.</h3>
                            <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto">
                                Try relaxing filters — most Passports on Simonara score 70+ Trust.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {data.results.map((c) => (
                                    <CandidateCard
                                        key={c.id}
                                        c={c}
                                        isFavorite={c.favorite}
                                        request={requestByCandidate.get(c.id)}
                                        onFavorite={toggleFavorite}
                                        onReachOut={openReach}
                                        onViewPassport={openPassport}
                                        onMessage={openMessage}
                                        onUnlockContact={handleUnlock}
                                        unlocked={unlockState.unlocked_talent_ids.includes(c.talent_user_id)}
                                        quotaRemaining={unlockState.unlimited ? "unlimited" : unlockState.remaining}
                                    />
                                ))}
                            </div>
                            {totalPages > 1 && (
                                <div className="mt-6 flex items-center justify-center gap-2" data-testid="marketplace-pagination">
                                    <button
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        className="btn-outline text-xs px-3 py-1.5 disabled:opacity-50"
                                        data-testid="marketplace-prev"
                                    >
                                        Prev
                                    </button>
                                    <span className="text-xs text-muted-foreground">Page {page} of {totalPages}</span>
                                    <button
                                        disabled={page >= totalPages}
                                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                        className="btn-outline text-xs px-3 py-1.5 disabled:opacity-50"
                                        data-testid="marketplace-next"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Reach-out dialog */}
            <Dialog open={!!reachTarget} onOpenChange={(o) => !o && setReachTarget(null)}>
                <DialogContent data-testid="marketplace-reach-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Send size={16} className="text-emerald-500" /> Reach out to {reachTarget?.initials}
                        </DialogTitle>
                        <DialogDescription>
                            Our talent team will contact this candidate on your behalf and share their full Talent Passport once they consent.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Role you&apos;re hiring for</label>
                            <Input
                                value={reachRole}
                                onChange={(e) => setReachRole(e.target.value)}
                                placeholder="e.g. Senior Customer Success Lead"
                                data-testid="marketplace-reach-role"
                                className="mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Note to our team (optional)</label>
                            <Textarea
                                value={reachNote}
                                onChange={(e) => setReachNote(e.target.value)}
                                placeholder="Anything specific? Budget, urgency, must-haves…"
                                data-testid="marketplace-reach-note"
                                rows={4}
                                className="mt-1"
                            />
                        </div>
                        {reachTarget && (
                            <div className="text-[11px] text-muted-foreground bg-muted/50 rounded-lg p-3 border border-border">
                                <div><span className="font-semibold text-foreground">Position:</span> {reachTarget.desired_position || "—"}</div>
                                <div><span className="font-semibold text-foreground">Location:</span> {reachTarget.city || "—"}</div>
                                {reachTarget.trust_breakdown && null}
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <button onClick={() => setReachTarget(null)} className="btn-outline text-sm px-4 py-2" data-testid="marketplace-reach-cancel">
                            Cancel
                        </button>
                        <button
                            onClick={submitReach}
                            disabled={reachSubmitting || !reachRole.trim()}
                            className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid="marketplace-reach-submit"
                        >
                            {reachSubmitting ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Send reach-out
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Full Passport dialog — only reachable after candidate consented */}
            <Dialog open={!!passportRequest} onOpenChange={(o) => { if (!o) { setPassportRequest(null); setPassportData(null); } }}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="marketplace-passport-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ShieldCheck size={16} className="text-emerald-500" />
                            Full Talent Passport
                        </DialogTitle>
                        <DialogDescription>
                            Candidate has consented to share their Passport with you.
                        </DialogDescription>
                    </DialogHeader>
                    {passportLoading ? (
                        <div className="py-10 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
                    ) : passportData?.candidate ? (
                        <div className="space-y-4 py-2 text-sm">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <PassportField label="Name" value={passportData.candidate.name} />
                                <PassportField label="Email" value={passportData.candidate.email} />
                                <PassportField label="Phone" value={passportData.candidate.phone} />
                                <PassportField label="City" value={passportData.candidate.city} />
                                <PassportField label="Desired role" value={passportData.candidate.desired_position} />
                                <PassportField label="Experience" value={passportData.candidate.years_experience} />
                                <PassportField label="Availability" value={passportData.candidate.availability} />
                                <PassportField label="Desired salary" value={passportData.candidate.desired_salary} />
                                <PassportField label="Languages" value={passportData.candidate.languages_spoken} />
                                <PassportField label="Work auth" value={passportData.candidate.work_authorization} />
                                {passportData.candidate.linkedin_url && (
                                    <PassportField label="LinkedIn" value={
                                        <a href={passportData.candidate.linkedin_url} target="_blank" rel="noreferrer" className="text-emerald-600 underline">
                                            View profile
                                        </a>
                                    } />
                                )}
                            </div>
                            {passportData.candidate.resume_excerpt && (
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">Resume</div>
                                    <div className="rounded-lg bg-muted p-3 text-xs whitespace-pre-wrap max-h-64 overflow-y-auto">
                                        {passportData.candidate.resume_excerpt}
                                    </div>
                                </div>
                            )}
                            {/* iter 149 — Identity verification request panel */}
                            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="marketplace-idv-panel">
                                <div className="flex items-start gap-3 flex-wrap">
                                    <ShieldCheck size={18} className="text-emerald-600 flex-none mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-heading font-semibold text-sm text-foreground">
                                            Government-ID verification
                                        </div>
                                        {idvStatus?.verified ? (
                                            <p className="mt-1 text-xs text-emerald-700 dark:text-emerald-400" data-testid="marketplace-idv-verified">
                                                ✓ This candidate has completed a Stripe Identity check
                                                {idvStatus.verified_at ? ` on ${new Date(idvStatus.verified_at).toLocaleDateString()}` : ""}.
                                            </p>
                                        ) : idvStatus?.requested_by_me ? (
                                            <p className="mt-1 text-xs text-muted-foreground" data-testid="marketplace-idv-requested-by-me">
                                                You requested an ID check on {idvStatus.requested_at ? new Date(idvStatus.requested_at).toLocaleDateString() : "this candidate"}. We've pinged them by in-app notification + email. You'll see the badge here when they finish.
                                            </p>
                                        ) : idvStatus?.requested ? (
                                            <p className="mt-1 text-xs text-muted-foreground" data-testid="marketplace-idv-requested-other">
                                                Another employer already has an ID check request open with this candidate. When they complete it, you'll see the verified badge too.
                                            </p>
                                        ) : (
                                            <p className="mt-1 text-xs text-muted-foreground">
                                                Request a government-ID + selfie check via Stripe Identity. Simonara covers the $1.50 cost — the candidate is not billed.
                                            </p>
                                        )}
                                    </div>
                                    {!idvStatus?.verified && !idvStatus?.requested && (
                                        <button
                                            onClick={requestIdVerification}
                                            disabled={idvSubmitting || !idvStatus}
                                            className="btn-green text-xs px-3 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                                            data-testid="marketplace-idv-request-btn"
                                        >
                                            {idvSubmitting ? <><Loader2 size={12} className="animate-spin" /> Sending…</> : <><ShieldCheck size={12} /> Request identity verification</>}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="py-8 text-center text-sm text-muted-foreground">Passport unavailable.</div>
                    )}
                </DialogContent>
            </Dialog>
            {/* iter-165 — compose a new message to a candidate */}
            <MessageComposeDialog
                open={!!messageTarget}
                onOpenChange={(o) => !o && setMessageTarget(null)}
                talentUserId={messageTarget?.talent_user_id}
                talentLabel={messageTarget?.initials ? `candidate ${messageTarget.initials}` : "this candidate"}
            />
        </div>
    );
};

const PassportField = ({ label, value }) => (
    <div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-foreground">{value || <span className="text-muted-foreground">—</span>}</div>
    </div>
);

export default TalentMarketplace;
